import { Center, Link as ChakraLink, Spinner, Text } from '@chakra-ui/react';
import React, { type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { Portfolio } from '../../_openapi/portfolio-optimizer';
import { usePortfolio } from '../../api/portfolio';
import { getColumnLabel } from '../../utils/tableUtils';
import { BooleanCell } from '../../widgets/Table/BooleanCell';
import { DateCell } from '../../widgets/Table/DateCell';
import { DateTimeCell } from '../../widgets/Table/DateTimeCell';
import { MoneyCell } from '../../widgets/Table/MoneyCell';
import { NumberCell } from '../../widgets/Table/NumberCell';
import { TextCell } from '../../widgets/Table/TextCell';
import { PortfolioInfoGrid } from '../PortfolioInfoGrid';

export type DataGridLine = {
    label: string;
    value: ReactNode;
};

export const createLinesPortfolioIdentification = (data?: Portfolio): DataGridLine[] => [
    { label: getColumnLabel('pId'), value: data?.base.pId || '' },
    { label: getColumnLabel('aC'), value: data?.base.aC || '' },
    {
        label: getColumnLabel('portfolioName'),
        value: data?.base.portfolioGuid ? (
            <ChakraLink
                as={Link}
                to={`/account/${data.base.portfolioGuid}`}
                color="teal.400"
                textDecoration="none"
                isExternal={true}
                _hover={{ textDecoration: 'underline' }}
            >
                {data?.base.portfolioName || ''}
            </ChakraLink>
        ) : (
            data?.base.portfolioName || ''
        ),
    },
    { label: getColumnLabel('portfolioGuid'), value: data?.base.portfolioGuid || '' },
    { label: getColumnLabel('brokerId'), value: data?.base.brokerId || '' },
    {
        label: getColumnLabel('householdName'),
        value: data?.base.householdGuid ? (
            <ChakraLink
                as={Link}
                to={`/household/${data.base.householdGuid}`}
                color="teal.400"
                textDecoration="none"
                isExternal={true}
                _hover={{ textDecoration: 'underline' }}
            >
                {data?.base.householdName || ''}
            </ChakraLink>
        ) : (
            data?.base.householdName || ''
        ),
    },
];

export const createLinesInvestmentSetup = (data?: Portfolio): DataGridLine[] => [
    {
        label: getColumnLabel('referencePortfolio'),
        value: <TextCell value={data?.base.referencePortfolio} />,
    },
    { label: getColumnLabel('accountType'), value: <TextCell value={data?.base.accountType} /> },
    {
        label: getColumnLabel('isTaxable'),
        value: <BooleanCell displayFalse textAlign="left" value={data?.base.isTaxable} />,
    },
    {
        label: getColumnLabel('effectiveTaxRate'),
        value: <NumberCell fractionDigits={1} style="percent" textAlign="left" value={data?.base.effectiveTaxRate} />,
    },
    {
        label: getColumnLabel('initialSecuredness'),
        value: <NumberCell fractionDigits={1} style="percent" textAlign="left" value={data?.base.initialSecuredness} />,
    },
    {
        label: getColumnLabel('isTaxLossHarvesting'),
        value: <BooleanCell displayFalse textAlign="left" value={data?.base.isTaxLossHarvesting} />,
    },
];

export const createLinesPortfolioStatus = (data?: Portfolio): DataGridLine[] => [
    {
        label: getColumnLabel('isSuspended'),
        value: <BooleanCell displayFalse textAlign="left" value={data?.base.isSuspended} />,
    },
    {
        label: getColumnLabel('suspendedAt'),
        value: <DateTimeCell dateString={data?.base.suspendedAt} />,
    },
    {
        label: getColumnLabel('isClientDirected'),
        value: <BooleanCell displayFalse textAlign="left" value={data?.base.isClientDirected} />,
    },
    {
        label: getColumnLabel('isOnboarded'),
        value: <BooleanCell displayFalse textAlign="left" value={data?.base.isOnboarded} />,
    },
    {
        label: getColumnLabel('isMirrorLinked'),
        value: <BooleanCell displayFalse textAlign="left" value={data?.base.isMirrorLinked} />,
    },
    {
        label: getColumnLabel('linkedDate'),
        value: <DateCell dateString={data?.base.linkedDate} />,
    },
    {
        label: getColumnLabel('openDate'),
        value: <DateCell dateString={data?.base.openDate} />,
    },
    { label: getColumnLabel('tradeDate'), value: <DateCell dateString={data?.base.tradeDate} /> },
    { label: getColumnLabel('ndvrPortfolioType'), value: <TextCell value={data?.base.ndvrPortfolioType} /> },
    {
        label: getColumnLabel('isCashOnly'),
        value: <BooleanCell displayFalse textAlign="left" value={data?.base.isCashOnly} />,
    },
    {
        label: getColumnLabel('isTestAccount'),
        value: <BooleanCell displayFalse textAlign="left" value={data?.base.isTestAccount} />,
    },
    {
        label: getColumnLabel('isUniverseAccount'),
        value: <BooleanCell displayFalse textAlign="left" value={data?.base.isUniverseAccount} />,
    },
];

export const createLinesPortfolioFigures = (data?: Portfolio): DataGridLine[] => [
    { label: getColumnLabel('nav'), value: <MoneyCell textAlign="left" fractionDigits={0} value={data?.base.nav} /> },
    { label: getColumnLabel('cash'), value: <MoneyCell textAlign="left" fractionDigits={0} value={data?.base.cash} /> },
    {
        label: getColumnLabel('cashFuturesMargin'),
        value: <MoneyCell textAlign="left" fractionDigits={0} value={data?.base.cashFuturesMargin} />,
    },
];

export const PortfolioDetailsTab = ({ pId }: { pId: string }) => {
    const { data, isPending } = usePortfolio({ pId });

    if (isPending) {
        return (
            <Center width="100%" height="40vh">
                <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="teal.500" size="lg" />
            </Center>
        );
    }

    return data ? <PortfolioInfoGrid portfolioDetails={data} /> : null;
};
